import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Sparkles, ArrowRight } from 'lucide-react';
import { storageGet, storageSet } from '../lib/safeStorage';
import { trackEvent } from '../lib/tracking';

const SHOWN_KEY = 'maxq_exit_intent_shown';
const EXCLUDED = ['/start', '/contact', '/bedankt', '/renovatie-ontwerpen', '/admin'];
const ENGAGEMENT_MS = 2000;

const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const restoreRef = useRef(null);

  const excluded = EXCLUDED.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (excluded) return undefined;
    if (storageGet('sessionStorage', SHOWN_KEY)) return undefined;
    if (!window.matchMedia || !window.matchMedia('(pointer: fine)').matches) return undefined;
    const mountedAt = Date.now();
    const onMouseOut = (e) => {
      if (e.relatedTarget !== null || e.clientY > 0) return; // only a real exit through the top edge
      if (Date.now() - mountedAt < ENGAGEMENT_MS) return;
      if (storageGet('sessionStorage', SHOWN_KEY)) return;
      storageSet('sessionStorage', SHOWN_KEY, '1');
      restoreRef.current = document.activeElement;
      setOpen(true);
      trackEvent('exit_popup_impression', { page_path: window.location.pathname });
    };
    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [location.pathname, excluded]);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const nodes = dialogRef.current.querySelectorAll('a[href], button:not([disabled])');
      const first = nodes[0]; const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    setOpen(false);
    const el = restoreRef.current;
    if (el && typeof el.focus === 'function') setTimeout(() => el.focus(), 0);
  };

  if (!open || excluded) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in" onClick={close} data-testid="exit-intent-popup">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="exit-popup-title" className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button ref={closeRef} onClick={close} className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-gray-500 hover:text-[#3a190b] transition-colors" aria-label="Sluiten" data-testid="exit-popup-close">
          <X className="h-5 w-5" />
        </button>
        <div className="bg-gradient-to-br from-[#3a190b] to-[#500000] text-white p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-3 py-1 mb-4 text-xs uppercase tracking-widest"><Sparkles className="h-3 w-3" /> Gratis AI-ontwerp</div>
          <h2 id="exit-popup-title" className="text-2xl sm:text-3xl font-bold mb-2 leading-tight">Benieuwd hoe uw ruimte er vernieuwd uitziet?</h2>
          <p className="text-white/80 text-sm sm:text-base">Upload een foto van uw badkamer, keuken of woonkamer en ontdek gratis een AI-ontwerp van uw eigen ruimte.</p>
        </div>
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-2">
          <Link to="/renovatie-ontwerpen" onClick={() => { trackEvent('exit_popup_click', { page_path: window.location.pathname }); close(); }} className="flex-1 bg-[#3a190b] hover:bg-[#500000] text-white px-5 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-colors" data-testid="exit-popup-cta">
            Bekijk mijn ruimte vernieuwd <ArrowRight className="h-4 w-4" />
          </Link>
          <button onClick={close} className="text-gray-500 hover:text-[#3a190b] text-sm px-4 py-3" data-testid="exit-popup-dismiss">Nee, bedankt</button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
